const STORAGE_KEY = 'a1looks_rec_engine';

export const CATEGORY_SLUGS = ['juttis', 'earrings', 'rings', 'necklaces', 'bracelets'];

// Event weights: stronger signals get higher weights
// product_view=6 (E1), wishlist_add=9 (E2), cart_add=12 (E3), purchase=18 (E4)
export const EVENT_WEIGHTS = {
  product_view: 6,
  wishlist_add: 9,
  cart_add: 12,
  purchase: 18,
};

// Temporal decay: recent behaviour matters more
// today=1x, this_week=0.5x, past(8-30 days)=0.33x
const MULTIPLIERS = { today: 1, this_week: 0.5, past: 1 / 3 };

function getTodayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(olderDate, newerDate) {
  return Math.floor((new Date(newerDate) - new Date(olderDate)) / 86400000);
}

function makeInitialData() {
  const categories = {};
  CATEGORY_SLUGS.forEach(s => {
    categories[s] = { today: 0, this_week: 0, past: 0 };
  });
  return { categories, lastSyncDate: getTodayISO() };
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return makeInitialData();
    const data = JSON.parse(raw);
    if (!data.categories) return makeInitialData();
    CATEGORY_SLUGS.forEach(s => {
      if (!data.categories[s]) data.categories[s] = { today: 0, this_week: 0, past: 0 };
    });
    return data;
  } catch {
    return makeInitialData();
  }
}

function persist(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

// Shift temporal buckets when date changes:
// today → this_week, this_week → past, past expires after 30 days.
// Uses accumulator pattern: O(1) time per sync (only 5 categories).
function applySync(data, todayISO) {
  const d = daysBetween(data.lastSyncDate || todayISO, todayISO);
  if (d <= 0) return;

  CATEGORY_SLUGS.forEach(s => {
    const c = data.categories[s];
    if (d >= 30) {
      c.today = 0; c.this_week = 0; c.past = 0;
    } else if (d >= 7) {
      c.past = c.today + c.this_week + c.past;
      c.this_week = 0; c.today = 0;
    } else {
      c.past += c.this_week;
      c.this_week = c.today;
      c.today = 0;
    }
  });

  data.lastSyncDate = todayISO;
}

// Push a user event — O(1): only updates the relevant category bucket.
export function trackEvent(categorySlug, eventType) {
  if (!CATEGORY_SLUGS.includes(categorySlug)) return;
  const weight = EVENT_WEIGHTS[eventType];
  if (!weight) return;

  const data = load();
  const today = getTodayISO();
  applySync(data, today);
  data.categories[categorySlug].today += weight;
  persist(data);
}

// Returns { w_juttis: n, w_earrings: n, ... } for the recommendation API.
export function getWeightParams() {
  const data = load();
  const today = getTodayISO();
  applySync(data, today);
  persist(data);

  const params = {};
  CATEGORY_SLUGS.forEach(s => {
    const c = data.categories[s];
    params[`w_${s}`] = +(
      c.today * MULTIPLIERS.today +
      c.this_week * MULTIPLIERS.this_week +
      c.past * MULTIPLIERS.past
    ).toFixed(2);
  });
  return params;
}

export function hasAnyWeight() {
  return Object.values(getWeightParams()).some(v => v > 0);
}
